// src/contexts/ProductionControlRoomContext.tsx
// Contexte opérationnel de la régie : SDP log, alertes, panels UI.
// Séparé de MediaStreamContext (qui gère les streams/codec/slots).
// v2.1 — ajout de restoreSdpLog pour restauration snapshot
// v3.0 — le log SDP sort du chemin de rendu : ref comme source de vérité, miroir
//        React publié uniquement panneau ouvert et throttlé, getter `getSdpLogRegie`
//        pour les lecteurs hors rendu. Valeur du contexte mémoïsée.

import React, {
  createContext, useContext, useState, useRef, useCallback, useEffect, useMemo,
  ReactNode, JSX,
} from 'react';

import { SdpLogEntry as SdpLogEntryRegie } from '../dataTypes/liveStreamManagment/types_sdp';
import { RemoteDeviceInfo, WhipNegotiationInfo } from '../dataTypes/liveStreamManagment/types_webrtc';

// ── Constantes SDP log ────────────────────────────────────────────────────────

/** Nombre maximum d'entrées conservées dans le log SDP de la régie. */
const SDP_LOG_MAX = 80;

/**
 * Période minimale entre deux publications du miroir React du log SDP, quand le
 * panneau de debug est ouvert. Les événements WebRTC arrivent par rafales (un par
 * candidat ICE) : sans ce plafond, chacun re-rendrait tout l'arbre de la régie.
 */
const SDP_PUBLISH_MS = 400;

// ── Types fenêtre d'alerte ────────────────────────────────────────────────────

type WindowImportance = 'normal' | 'important';
type WindowType       = 'information' | 'question';

interface WindowState {
  open              : boolean;
  message           : JSX.Element | null;
  importance        : WindowImportance;
  type              : WindowType;
  onValidate        : () => void;
  onCancel?         : () => void;
  validButtonText?  : string;
  cancelButtonText? : string;
  // Bouton secondaire optionnel (ex : « Acheter plus d'espace »), à gauche des autres.
  extraButtonText?  : string;
  onExtra?          : () => void;
  width?            : string;
}

/**
 * Options de personnalisation de la fenêtre d'information (showAlert).
 * Le bouton secondaire est rendu complètement à gauche par le widget Window.
 */
export interface AlertOptions {
  validButtonText?  : string;
  extraButtonText?  : string;
  /** Appelé APRÈS fermeture de la fenêtre (il peut donc en rouvrir une). */
  onExtra?          : () => void;
  width?            : string;
}

/** Options de personnalisation de la fenêtre de confirmation. */
export interface ConfirmOptions {
  validButtonText?  : string;
  cancelButtonText? : string;
  extraButtonText?  : string;
  /** Appelé après fermeture de la fenêtre — la confirmation est donc abandonnée. */
  onExtra?          : () => void;
  /** Appelé après fermeture par le bouton d'annulation (« Non ») — 2026-10-24. */
  onCancel?         : () => void;
  width?            : string;
}

const WINDOW_CLOSED: WindowState = {
  open: false, message: null, importance: 'normal',
  type: 'information', onValidate: () => {}, onCancel: undefined,
  validButtonText: undefined, cancelButtonText: undefined,
  extraButtonText: undefined, onExtra: undefined, width: undefined,
};

// ── Interface du contexte ─────────────────────────────────────────────────────

interface ProductionControlRoomContextType {
  // ── Alertes / confirmations ───────────────────────────────────────────────
  windowState : WindowState;
  showAlert   : (message: string | JSX.Element, importance?: WindowImportance, options?: AlertOptions) => void;
  showConfirm : (message: string | JSX.Element, onConfirm: () => void, options?: ConfirmOptions) => void;

  // ── SDP Debug ─────────────────────────────────────────────────────────────
  /**
   * Miroir React du log, publié uniquement quand `showSdpDebugRegie` est vrai
   * (et throttlé). Pour lire le log en dehors du rendu, utiliser `getSdpLogRegie`.
   */
  sdpLogRegie          : SdpLogEntryRegie[];
  /** Lecture directe du log courant, sans abonnement aux rendus. */
  getSdpLogRegie       : () => SdpLogEntryRegie[];
  addSdpLogRegie       : (entry: Omit<SdpLogEntryRegie, 'id' | 'timestamp'>) => void;
  clearSdpLog          : () => void;
  restoreSdpLog        : (entries: SdpLogEntryRegie[]) => void; // ← v2.1
  showSdpDebugRegie    : boolean;
  setShowSdpDebugRegie : (v: boolean) => void;
  sdpInitialDevice     : string;
  setSdpInitialDevice  : (v: string) => void;

  // ── Device info panel ─────────────────────────────────────────────────────
  showDeviceInfoPanel    : boolean;
  setShowDeviceInfoPanel : (v: boolean) => void;
  activeDeviceInfo       : RemoteDeviceInfo | null;
  setActiveDeviceInfo    : (info: RemoteDeviceInfo | null) => void;

  // ── WHIP info panel ───────────────────────────────────────────────────────
  showWhipInfoPanel    : boolean;
  setShowWhipInfoPanel : (v: boolean) => void;
  whipInfo             : WhipNegotiationInfo;
  setWhipInfo          : React.Dispatch<React.SetStateAction<WhipNegotiationInfo>>;

  // ── Ref partagée (infos négociées par device) ─────────────────────────────
  remoteDeviceInfoRef : React.MutableRefObject<Map<string, RemoteDeviceInfo>>;
}

// ── Création du contexte ──────────────────────────────────────────────────────

const ProductionControlRoomContext = createContext<
  ProductionControlRoomContextType | undefined
>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

export const ProductionControlRoomProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

  // ── Compteur SDP log (muté directement, pas de state) ─────────────────────
  const sdpLogCounterRef = useRef(0);

  // ── Alertes ───────────────────────────────────────────────────────────────
  const [windowState, setWindowState] = useState<WindowState>(WINDOW_CLOSED);

  const showAlert = useCallback((
    message: string | JSX.Element,
    importance: WindowImportance = 'normal',
    options?: AlertOptions
  ) => {
    setWindowState({
      open: true,
      message: typeof message === 'string' ? <span>{message}</span> : message,
      importance,
      type: 'information',
      onValidate: () => setWindowState(WINDOW_CLOSED),
      onCancel: undefined,
      validButtonText : options?.validButtonText,
      // Le bouton secondaire ferme la fenêtre avant d'agir : son action peut en
      // rouvrir une (message d'achat), et deux fenêtres ne cohabitent pas.
      onExtra         : options?.onExtra
        ? () => { setWindowState(WINDOW_CLOSED); options.onExtra!(); }
        : undefined,
      extraButtonText : options?.extraButtonText,
      width           : options?.width,
    });
  }, []);

  const showConfirm = useCallback((
    message: string | JSX.Element,
    onConfirm: () => void,
    options?: ConfirmOptions
  ) => {
    setWindowState({
      open: true,
      message: typeof message === 'string' ? <span>{message}</span> : message,
      importance: 'important',
      type: 'question',
      onValidate: () => { setWindowState(WINDOW_CLOSED); onConfirm(); },
      // Fermer d'abord : l'action d'annulation peut ouvrir une autre fenêtre.
      onCancel: () => { setWindowState(WINDOW_CLOSED); options?.onCancel?.(); },
      validButtonText : options?.validButtonText,
      cancelButtonText: options?.cancelButtonText,
      extraButtonText : options?.extraButtonText,
      // Le bouton secondaire ferme la fenêtre : la confirmation est abandonnée.
      onExtra         : options?.onExtra
        ? () => { setWindowState(WINDOW_CLOSED); options.onExtra!(); }
        : undefined,
      width           : options?.width,
    });
  }, []);

  // ── SDP Log ───────────────────────────────────────────────────────────────
  // v3.0 — le log est écrit à chaque événement WebRTC : un appel par candidat ICE,
  // par changement de connectionState / iceConnectionState / signalingState, etc.
  // Une négociation en produit des dizaines à des centaines en deux ou trois
  // secondes. Tant qu'il vivait dans un state React, chaque entrée changeait
  // l'identité de la valeur du contexte et re-rendait tout l'arbre de la régie
  // (LiveStreamManager, Deck, ControlBar — aucun n'est mémoïsé) : le thread
  // principal était saturé pendant toute la phase de connexion.
  //
  // Source de vérité : `sdpLogRef`, mutée sans provoquer de rendu. Le miroir
  // `sdpLogRegie` n'est publié que si le panneau de debug est ouvert, et au plus
  // une fois toutes les SDP_PUBLISH_MS. Panneau fermé — le cas courant côté
  // régie — l'écriture du log ne coûte donc plus aucun rendu.
  const sdpLogRef = useRef<SdpLogEntryRegie[]>([]);
  const [sdpLogRegie, setSdpLogRegie] = useState<SdpLogEntryRegie[]>([]);
  const [showSdpDebugRegie, setShowSdpDebugRegie] = useState(false);
  const [sdpInitialDevice, setSdpInitialDevice]   = useState<string>('all');

  const sdpPublishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showSdpDebugRef    = useRef(showSdpDebugRegie);

  /** Publie immédiatement le miroir React (ouverture du panneau, clear, restore). */
  const publishSdpLog = useCallback(() => {
    if (sdpPublishTimerRef.current) {
      clearTimeout(sdpPublishTimerRef.current);
      sdpPublishTimerRef.current = null;
    }
    setSdpLogRegie(sdpLogRef.current);
  }, []);

  /** Programme une publication throttlée — sans effet si le panneau est fermé. */
  const scheduleSdpPublish = useCallback(() => {
    if (!showSdpDebugRef.current)      return; // personne ne lit le miroir
    if (sdpPublishTimerRef.current)    return; // publication déjà programmée
    sdpPublishTimerRef.current = setTimeout(() => {
      sdpPublishTimerRef.current = null;
      setSdpLogRegie(sdpLogRef.current);
    }, SDP_PUBLISH_MS);
  }, []);

  // Le flag est lu depuis les callbacks : il lui faut un miroir en ref.
  // À l'ouverture, on publie ce qui a été accumulé pendant que le panneau était fermé.
  useEffect(() => {
    showSdpDebugRef.current = showSdpDebugRegie;
    if (showSdpDebugRegie) publishSdpLog();
  }, [showSdpDebugRegie, publishSdpLog]);

  useEffect(() => () => {
    if (sdpPublishTimerRef.current) clearTimeout(sdpPublishTimerRef.current);
  }, []);

  const addSdpLogRegie = useCallback((
    entry: Omit<SdpLogEntryRegie, 'id' | 'timestamp'>
  ) => {
    sdpLogRef.current = [{
      ...entry,
      id: ++sdpLogCounterRef.current,
      timestamp: new Date().toLocaleTimeString('fr-FR', { hour12: false } as any),
    }, ...sdpLogRef.current].slice(0, SDP_LOG_MAX);
    scheduleSdpPublish();
  }, [scheduleSdpPublish]);

  /**
   * Lecture du log sans passer par le miroir React — donc sans s'abonner aux
   * rendus. Utilisé par useLiveStreamSnapshot, qui n'a besoin de la valeur qu'au
   * moment d'écrire dans localStorage.
   */
  const getSdpLogRegie = useCallback(() => sdpLogRef.current, []);

  const clearSdpLog = useCallback(() => {
    sdpLogRef.current = [];
    publishSdpLog();
  }, [publishSdpLog]);

  // ── v2.1 : restauration bulk du log depuis un snapshot ────────────────────
  // Utilisé exclusivement par useLiveStreamSnapshot au remontage du composant.
  const restoreSdpLog = useCallback((entries: SdpLogEntryRegie[]) => {
    sdpLogRef.current = entries.slice(0, SDP_LOG_MAX);
    // Recale le compteur pour éviter les collisions d'id lors des prochains addSdpLogRegie
    const maxId = entries.reduce((m, e) => Math.max(m, e.id ?? 0), 0);
    sdpLogCounterRef.current = maxId;
    publishSdpLog();
  }, [publishSdpLog]);

  // ── Device info panel ─────────────────────────────────────────────────────
  const [showDeviceInfoPanel, setShowDeviceInfoPanel] = useState(false);
  const [activeDeviceInfo, setActiveDeviceInfo]       = useState<RemoteDeviceInfo | null>(null);

  // ── WHIP info panel ───────────────────────────────────────────────────────
  const [showWhipInfoPanel, setShowWhipInfoPanel] = useState(false);
  const [whipInfo, setWhipInfo]                   = useState<WhipNegotiationInfo>({});

  // ── Ref infos device ──────────────────────────────────────────────────────
  const remoteDeviceInfoRef = useRef<Map<string, RemoteDeviceInfo>>(new Map());

  // ── Valeur du contexte ────────────────────────────────────────────────────

  // Mémoïsée : sans cela, un rendu du provider (même provoqué par son parent)
  // recrée l'objet et re-rend tous les consommateurs, dont LiveStreamManager.
  const value: ProductionControlRoomContextType = useMemo(() => ({
    windowState, showAlert, showConfirm,
    sdpLogRegie, getSdpLogRegie, addSdpLogRegie, clearSdpLog, restoreSdpLog,
    showSdpDebugRegie, setShowSdpDebugRegie,
    sdpInitialDevice, setSdpInitialDevice,
    showDeviceInfoPanel, setShowDeviceInfoPanel,
    activeDeviceInfo, setActiveDeviceInfo,
    showWhipInfoPanel, setShowWhipInfoPanel,
    whipInfo, setWhipInfo,
    remoteDeviceInfoRef,
  }), [
    windowState, showAlert, showConfirm,
    sdpLogRegie, getSdpLogRegie, addSdpLogRegie, clearSdpLog, restoreSdpLog,
    showSdpDebugRegie, sdpInitialDevice,
    showDeviceInfoPanel, activeDeviceInfo,
    showWhipInfoPanel, whipInfo,
    remoteDeviceInfoRef,
  ]);

  return (
    <ProductionControlRoomContext.Provider value={value}>
      {children}
    </ProductionControlRoomContext.Provider>
  );
};

// ── Hook consommateur ─────────────────────────────────────────────────────────

export const useProductionControlRoom = (): ProductionControlRoomContextType => {
  const ctx = useContext(ProductionControlRoomContext);
  if (!ctx) throw new Error(
    'useProductionControlRoom must be used within ProductionControlRoomProvider'
  );
  return ctx;
};
